// DEV-214: camera capture as a reusable composable — same overlay UX as the
// damage-photo flow in ChecksDamageView (DEV-200). Opens the rear camera in a
// fullscreen overlay and resolves one JPEG dataURL (quality 0.8, max width
// 1280) or null on cancel/error.
// DEV-216: surface the real getUserMedia error (German, actionable) instead of
// swallowing it, and fall back to the native camera app via <input capture>
// when in-browser camera access is unavailable or blocked — the prod failure
// mode that left dispatchers unable to photograph the Tank-Beleg.
import { ref } from 'vue'

const MAX_WIDTH = 1280
const JPEG_QUALITY = 0.8
const METADATA_TIMEOUT_MS = 8000

export function useCameraCapture() {
  const cameraError = ref<string | null>(null)

  const errorMessage = (err: unknown): string => {
    switch ((err as DOMException)?.name) {
      case 'NotAllowedError':
      case 'SecurityError':
        return 'Kamerazugriff wurde blockiert. Bitte in den Browser-Einstellungen für diese Seite erlauben.'
      case 'NotFoundError':
      case 'OverconstrainedError':
        return 'Es wurde keine Kamera gefunden.'
      case 'NotReadableError':
        return 'Die Kamera wird gerade von einer anderen App verwendet.'
      default:
        return 'Das Foto konnte nicht aufgenommen werden. Bitte versuche es später erneut.'
    }
  }

  // Downscale + re-encode to JPEG. The backend only accepts real JPEG/PNG
  // bytes (finfo check), so an iPhone HEIC from the native picker must be
  // re-encoded here or the upload would 422.
  const fileToJpegDataUrl = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file)
      const img = new Image()
      img.onload = () => {
        const scale = img.naturalWidth > MAX_WIDTH ? MAX_WIDTH / img.naturalWidth : 1
        const canvas = document.createElement('canvas')
        canvas.width = Math.round(img.naturalWidth * scale)
        canvas.height = Math.round(img.naturalHeight * scale)
        canvas.getContext('2d')?.drawImage(img, 0, 0, canvas.width, canvas.height)
        URL.revokeObjectURL(url)
        resolve(canvas.toDataURL('image/jpeg', JPEG_QUALITY))
      }
      img.onerror = () => {
        URL.revokeObjectURL(url)
        reject(new Error('Bild konnte nicht dekodiert werden'))
      }
      img.src = url
    })

  // Native fallback: the OS camera app works even when in-browser camera
  // access is blocked.
  const openNativeCapture = (): Promise<string | null> =>
    new Promise((resolve) => {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = 'image/*'
      input.setAttribute('capture', 'environment')
      input.style.display = 'none'
      document.body.appendChild(input)

      const finish = (value: string | null) => {
        input.remove()
        resolve(value)
      }

      input.onchange = async () => {
        const file = input.files?.[0]
        if (!file) return finish(null)
        try {
          const photo = await fileToJpegDataUrl(file)
          cameraError.value = null
          finish(photo)
        } catch (err) {
          console.error('[camera] fallback decode failed:', err)
          cameraError.value = 'Dieses Bildformat wird nicht unterstützt.'
          finish(null)
        }
      }
      // Fired by modern browsers when the picker is dismissed without a file.
      input.addEventListener('cancel', () => finish(null))
      input.click()
    })

  const openOverlayCamera = async (stream: MediaStream): Promise<string | null> => {
    const video = document.createElement('video')
    video.srcObject = stream
    video.autoplay = true
    video.playsInline = true

    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')

    // Guard with a timeout: a metadata event that never fires used to hang the
    // promise forever and permanently disable the capture button (DEV-216).
    await new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('Kamera-Vorschau wurde nicht bereit')), METADATA_TIMEOUT_MS)
      video.onloadedmetadata = () => {
        clearTimeout(timer)
        let targetWidth = video.videoWidth
        let targetHeight = video.videoHeight
        if (video.videoWidth > MAX_WIDTH) {
          const scale = MAX_WIDTH / video.videoWidth
          targetWidth = MAX_WIDTH
          targetHeight = Math.round(video.videoHeight * scale)
        }
        canvas.width = targetWidth
        canvas.height = targetHeight
        resolve(true)
      }
    })

    const cameraModal = document.createElement('div')
    cameraModal.className = 'fixed inset-0 bg-black flex flex-col items-center justify-center z-50'

    const videoContainer = document.createElement('div')
    videoContainer.className = 'relative w-full h-full flex flex-col'
    videoContainer.appendChild(video)

    const controls = document.createElement('div')
    controls.className = 'absolute bottom-0 left-0 right-0 flex justify-center p-4 bg-black bg-opacity-50'

    const captureButton = document.createElement('button')
    captureButton.className = 'px-4 py-3 bg-[#E9041D] text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium'
    captureButton.textContent = 'Foto aufnehmen'

    const cancelButton = document.createElement('button')
    cancelButton.className = 'ml-2 px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium'
    cancelButton.textContent = 'Abbrechen'

    controls.appendChild(captureButton)
    controls.appendChild(cancelButton)
    videoContainer.appendChild(controls)
    cameraModal.appendChild(videoContainer)
    document.body.appendChild(cameraModal)

    return await new Promise<string | null>((resolve) => {
      captureButton.onclick = () => {
        context?.drawImage(video, 0, 0, canvas.width, canvas.height)
        const photoData = canvas.toDataURL('image/jpeg', JPEG_QUALITY)
        stream.getTracks().forEach(track => track.stop())
        document.body.removeChild(cameraModal)
        resolve(photoData)
      }

      cancelButton.onclick = () => {
        stream.getTracks().forEach(track => track.stop())
        document.body.removeChild(cameraModal)
        resolve(null)
      }
    })
  }

  const openCamera = async (): Promise<string | null> => {
    cameraError.value = null

    if (!navigator.mediaDevices?.getUserMedia) {
      // No in-browser camera API at all — go straight to the native app.
      return openNativeCapture()
    }

    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      })
    } catch (err) {
      console.error('[camera] getUserMedia failed:', err)
      // Keep the actionable message visible even if the fallback is cancelled,
      // so the user learns why the in-app camera did not open.
      cameraError.value = errorMessage(err)
      return openNativeCapture()
    }

    try {
      return await openOverlayCamera(stream)
    } catch (err) {
      console.error('[camera] overlay capture failed:', err)
      stream.getTracks().forEach(track => track.stop())
      cameraError.value = errorMessage(err)
      return openNativeCapture()
    }
  }

  return { openCamera, cameraError }
}
